import React, { useEffect, useMemo } from 'react';
import FilterBar from '../Common/FilterBar';
import { useConfig } from '../../DataStore/ConfigContext';
import { useStudent } from '../../DataStore/StudentContext';

const StudentListFilter = ({ handleExport, title="Student listing", description = "Filter and manage students" }) => {
  const { classes, getClasses, residences, getResidences } = useConfig();
  const { dispatch, filter, searchText, classId, residenceId, streamId } = useStudent();

  useEffect(() => {
    getClasses();
    getResidences();
  }, []);

  const residenceOptions = useMemo(() => {
    return (Array.isArray(residences) ? residences : []).map((r) => ({
      value: String(r.id),
      label: r.residence_name,
    }));
  }, [residences]);

  const classOptions = useMemo(() => {
    return (Array.isArray(classes) ? classes : []).map((c) => ({
      value: String(c.class_id),
      label: c.class_name,
    }));
  }, [classes]);

  // ✅ find selected class
  const selectedClass = useMemo(() => {
    const id = String(classId ?? "");
    return (Array.isArray(classes) ? classes : []).find((c) => String(c.class_id) === id) || null;
  }, [classes, classId]);

  // ✅ streams depend on selected class
  const streamOptions = useMemo(() => {
    const streams = selectedClass?.streams;
    if (!Array.isArray(streams)) return []; // handles false/null/undefined
    return streams.map((s) => ({
      value: String(s.stream_id),
      label: s.stream_name,
    }));
  }, [selectedClass]);

  const handleClassChange = (e) => {
    const newClassId = String(e.target.value);
    dispatch({ type: "classId", payload: newClassId });

    // ✅ reset stream when class changes (prevents mismatched stream)
    dispatch({ type: "streamId", payload: "" });
  };

  return (
    <FilterBar
      title={title}
      subtitle={description}
      controls={[
        {
          type: "search",
          value: searchText,
          placeholder: "Enter search hint...",
          width: 280,
          onChange: (e) => dispatch({ type: "searchText", payload: e.target.value }),
          onEnter: filter,
        },
        {
          type: "select",
          value: String(classId ?? ""),
          placeholder: "All classes..",
          width: 120,
          options: classOptions,
          onChange: handleClassChange,
        },
        {
          type: "select",
          value: String(streamId ?? ""),
          placeholder: selectedClass ? "All streams.." : "Select class first",
          width: 170,
          options: streamOptions,
          onChange: (e) => dispatch({ type: "streamId", payload: String(e.target.value) }),
          disabled: !selectedClass || streamOptions.length === 0, // if FilterBar supports it
        },
        {
          type: "select",
          value: String(residenceId ?? ""),
          placeholder: "All residences",
          width: 170,
          options: residenceOptions,
          onChange: (e) => dispatch({ type: "residenceId", payload: String(e.target.value) }),
        },
      ]}
      actions={[
        {
          label: "Export",
          iconClass: "fa fa-file-excel text-primary",
          onClick: handleExport,
          variant: "secondary",
        },
        {
          label: "Apply",
          iconClass: "fa fa-filter",
          onClick: filter,
          variant: "primary",
        },
      ]}
    />
  );
};

export default StudentListFilter;
